import React, { MouseEventHandler } from 'react';
import { GenericButton } from 'components';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { Theme } from '@app/theme';
import { useTrackSelect } from '@app/analytics';

import Duration10minGray from '@app/illu/Duration/Duration10minGray.svg';
import Duration10minGreen from '@app/illu/Duration/Duration10minGreen.svg';

import Duration20minGray from '@app/illu/Duration/Duration20minGray.svg';
import Duration20minGreen from '@app/illu/Duration/Duration20minGreen.svg';

import Duration30minGray from '@app/illu/Duration/Duration30minGray.svg';
import Duration30minGreen from '@app/illu/Duration/Duration30minGreen.svg';

import Duration45minGray from '@app/illu/Duration/Duration45minGray.svg';
import Duration45minGreen from '@app/illu/Duration/Duration45minGreen.svg';

import Duration60minGray from '@app/illu/Duration/Duration60minGray.svg';
import Duration60minGreen from '@app/illu/Duration/Duration60minGreen.svg';
import { Trans } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  durationButton: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    transition: 'width .3s ease',

    '&:hover': {
      background: theme.colors.grayBackground,
    },
  },
  small: {
    padding: theme.marginBase,
  },
  durationButton__labelHidden: {
    width: 74,
    gap: 0,
  },
  selected: {
    border: `1px solid ${theme.colors.green}`,
    backgroundColor: theme.colors.grayBackground,
  },
  'not-selected': {
    border: `1px solid ${theme.colors.grayBorder}`,
    backgroundColor: theme.colors.white,
  },
  label: {
    ...theme.fonts.h5,
    transition: 'opacity 1s ease',
    marginLeft: theme.marginBase * 2,
    whiteSpace: 'nowrap',
  },
  labelSmall: {
    ...theme.fonts.label,
    lineHeight: '100%',
  },
  label__hidden: {
    marginLeft: 0,
    opacity: 0,
    visibility: 'hidden',
    width: 0,
  },
}));

interface Props {
  duration: number;
  durationText?: string;
  onClick: MouseEventHandler;
  title: string;
  isSelected?: boolean;
  hideLabel?: boolean;
  small?: boolean;
  trackingName?: string;
}

const icons = {
  10: {
    gray: Duration10minGray,
    green: Duration10minGreen,
  },
  20: {
    gray: Duration20minGray,
    green: Duration20minGreen,
  },
  30: {
    gray: Duration30minGray,
    green: Duration30minGreen,
  },
  45: {
    gray: Duration45minGray,
    green: Duration45minGreen,
  },
  60: {
    gray: Duration60minGray,
    green: Duration60minGreen,
  },
};

const getDurationIcon = (duration: Props['duration'], isSelected?: boolean) => {
  if (!(duration in icons)) {
    return null;
  }
  const icon = icons[duration as keyof typeof icons];
  return isSelected ? icon.green : icon.gray;
};

export const DurationButton = ({
  duration,
  durationText,
  onClick,
  title,
  isSelected = false,
  hideLabel = false,
  small,
  trackingName,
}: Props) => {
  const theme = useTheme() as Theme;
  const classes = useStyles({ theme });

  const DurationIcon = getDurationIcon(duration, isSelected);

  const valueText = `${durationText || duration}`;

  const _onClick = useTrackSelect(onClick, [onClick]);

  return (
    <GenericButton
      className={classnames(
        'button',
        classes.durationButton,
        hideLabel ? classes.durationButton__labelHidden : '',
        isSelected ? classes.selected : classes['not-selected'],
        { [classes.small]: small },
      )}
      onClick={_onClick}
      title={title}
      type="button"
      data-tracking-name={trackingName}
    >
      {DurationIcon && <img src={DurationIcon} alt={valueText} />}

      <div
        className={classnames(
          classes.label,
          hideLabel ? classes.label__hidden : '',
          { [classes.labelSmall]: small },
        )}
        aria-label="Label"
      >
        {valueText}
        <Trans i18nKey="studySetup.generalSetup.duration.min" />
      </div>
    </GenericButton>
  );
};
